import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, of, lastValueFrom } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { AzureOpenAI } from 'openai';
import { ChatCompletionMessageParam } from 'openai/resources/index';
import { stringify } from 'qs';
import { FilterObject } from './filter-object';
import { EnvironmentsService } from '../environments/environments.service';

import { tools } from './tools';
import { PostRequestBody } from './schema';
import { cleanObject } from './clean-object';
import { IListingsResponse } from '../../interface/ilistings-response';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AiAgentService {
  private openai: AzureOpenAI;

  constructor(
    private http: HttpClient,
    private environmentService: EnvironmentsService
  ) {
    this.openai = new AzureOpenAI({
      apiKey: this.environmentService.azureOpenAIApiKey,
      dangerouslyAllowBrowser: true,
      endpoint: `https://out-ai.openai.azure.com/`,
      deployment: this.environmentService.azureOpenAIDeploymentName,
      apiVersion: '2024-04-01-preview',
    });
  }

  sendMessage(
    message: PostRequestBody['message'],
    messages: PostRequestBody['messages']
  ): Observable<any> {
    // Get relevant context from vector store
    return this.getVectorContext(message.parts[0].text.value).pipe(
      switchMap((siteContext) => {
        const conversationHistory = this.prepareConversationHistory(messages);

        const completion$ = from(
          this.openai.chat.completions.create({
            model: 'gpt-35-turbo',
            messages: [
              {
                role: 'system',
                content: this.getSystemPrompt(siteContext),
              },
              ...conversationHistory,
              {
                role: 'user',
                content: message.parts[0].text.value,
              },
            ],
            tools: tools as any[],
            stream: false,
          })
        );

        return completion$.pipe(
          switchMap((response) => {
            if (response.choices[0].message.tool_calls) {
              const toolCall = response.choices[0].message.tool_calls[0];
              if (toolCall.type === 'function') {
                const args = JSON.parse(toolCall.function.arguments);
                console.log('The args are:', args);
                if (toolCall.function.name === 'get_listings') {
                  const requiredGetListingFields = [
                    'location',
                    'model',
                    'year',
                    'condition',
                  ];
                  const missingFields = requiredGetListingFields.filter(
                    (field) =>
                      !args[field] ||
                      (typeof args[field] === 'string' &&
                        args[field].trim() === '')
                  );

                  console.log('The missing fields are:', missingFields);
                  if (missingFields.length > 0) {
                    // 2️⃣ Ask user for the NEXT missing field only
                    const nextField = missingFields[0];

                    return from(
                      this.openai.chat.completions.create({
                        model: 'gpt-35-turbo',
                        messages: [
                          {
                            role: 'system',
                            content: `You are a helpful assistant guiding the user to find Tesla listings.
- Always mention the information they have already provided.
- Ask ONLY for the **next missing field** in a natural, conversational way.
- Do NOT ask for multiple fields at once.
- The "condition" must be one of: Excellent, VeryGood, Good, Fair.
- The "year" must be a single year (e.g., 2022), not a range.
- Example:
  If the user said "I want a Model Y", you should respond like:
  "Great, you want a Model Y. What location would you like the car to be in?". the response doesnt need to be the exactly as I have provided`,
                          },
                          {
                            role: 'user',
                            content: `The user has already provided: ${JSON.stringify(args)}.
The next missing field is: ${nextField}`,
                          },
                        ],
                      })
                    ).pipe(
                      map((clarifyResponse) => ({
                        response: clarifyResponse.choices[0].message.content,
                      }))
                    );
                  }

                  return from(this.getListingsFunction(args)).pipe(
                    switchMap((filters) => {
                      const secondResponse$ = from(
                        this.openai.chat.completions.create({
                          model: 'gpt-35-turbo',
                          messages: [
                            {
                              role: 'system',
                              content:
                                'You are a helpful AI assistant tasked with summarizing search results.\n' +
                                '---\n' +
                                'GUIDELINES:\n' +
                                '* Do not include URLs or external links in your output.\n' +
                                '* Your response should be a brief, single-sentence summary of the search results.\n' +
                                '* The response should be based solely on the totalCount value from the provided data.\n' +
                                "* Example response format: 'I found a total of [number] listings for you. Please check them out below.'",
                            },
                            {
                              role: 'user',
                              content: message.parts[0].text.value,
                            },
                            response.choices[0].message,
                            {
                              role: 'tool',
                              tool_call_id: toolCall.id,
                              content: JSON.stringify(filters),
                            },
                          ],
                          stream: false,
                        })
                      );

                      return secondResponse$.pipe(
                        map((secondResponse) => ({
                          response: secondResponse.choices[0].message.content,
                          filters: filters,
                          Skip: 0,
                        }))
                      );
                    })
                  );
                }
                if (toolCall.function.name === 'create_listing') {
                  type SchemaProperties = Record<
                    string,
                    {
                      type: string | string[];
                      description?: string;
                      enum?: string[];
                      minimum?: number;
                      default?: string;
                      [key: string]: unknown;
                    }
                  >;

                  console.log('original args', args);

                  // Clone args to avoid modifying the original
                  const argsCopy = { ...args };
                  console.log('argsCopy', argsCopy);

                  // 1️⃣ Ignore leaseMonthsRemaining if type is "ForSale"
                  if (argsCopy.type === 'ForSale') {
                    console.log('Deleting leaseMonthsRemaining...');
                    delete argsCopy.leaseMonthsRemaining;
                  }

                  if (!argsCopy.autopilot) {
                    console.log('Deleting autopilotType...');
                    delete argsCopy.autopilotType;
                  }

                  console.log('argsCopy after', argsCopy);

                  // 2️⃣ Find missing fields
                  const missingFields = Object.entries(argsCopy)
                    .filter(
                      ([_, val]) =>
                        val === null ||
                        val === undefined ||
                        (typeof val === 'string' &&
                          (val.trim() === '' || val === 'null'))
                    )
                    .map(([key]) => ({
                      key,
                      description:
                        createListingFieldDescriptions[
                          key as keyof typeof createListingFieldDescriptions
                        ] || '',
                    }));

                  const allParamsProvided = missingFields.length === 0;

                  if (allParamsProvided) {
                    // ✅ All good — custom confirmation message
                    return from(
                      this.openai.chat.completions.create({
                        model: 'gpt-35-turbo',
                        messages: [
                          {
                            role: 'system',
                            content: `You are a helpful AI assistant.
When all required information for a vehicle listing is available:
1. Tell them: "I've got everything I need to help you list your vehicle. Once completed, you can upload images manually and complete the payment. Hit the button below to confirm."
2. Present the listing details they provided in a clean, two-column HTML table with borders, padding, and alternating row background colors for readability please make sure the table is taking the full width of the container.
3. Do NOT say the listing has been created yet.
4. If any value is null, display "Not Provided" instead.
5. Preserve the exact values given by the user without guessing or adding extra details.`,
                          },
                          {
                            role: 'user',
                            content: `Here’s the listing data: ${JSON.stringify(
                              argsCopy
                            )}`,
                          },
                        ],
                      })
                    ).pipe(
                      map((secondResponse) => ({
                        response: secondResponse.choices[0].message.content,
                        createListingData: argsCopy,
                        canCreateListing: true,
                      }))
                    );
                  } else {
                    const missingList = missingFields
                      .map(
                        (f) =>
                          `• **${f.key}** — ${
                            f.description || 'No description available'
                          }`
                      )
                      .join('\n');

                    return from(
                      this.openai.chat.completions.create({
                        model: 'gpt-35-turbo',
                        messages: [
                          {
                            role: 'system',
                            content: `You are a helpful assistant.
Politely tell the user that some information is missing for creating their listing.
Clearly list the missing fields in bullet points, including a short description for each.
Do NOT ask for anything not in the list.`,
                          },
                          {
                            role: 'user',
                            content: `The following fields are missing:\n${missingList}`,
                          },
                        ],
                      })
                    ).pipe(
                      map((secondResponse) => ({
                        response: secondResponse.choices[0].message.content,
                        createListingData: argsCopy,
                        canCreateListing: false,
                      }))
                    );
                  }
                }
              }
            }
            return of({ response: response.choices[0].message.content });
          })
        );
      }),
      catchError((error) => {
        console.error('Error:', error);
        if (error instanceof Error && error.name === 'AbortError') {
          return of({ error: 'Request cancelled' });
        }
        return of({ error: 'An unknown error occurred.' });
      })
    );
  }

  private getVectorContext(searchText: string): Observable<string> {
    const url = `${this.environmentService.azureAiSearchEndpoint}/indexes/${this.environmentService.azureAiSearchIndex}/docs/search?api-version=2025-05-01-Preview`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'api-key': this.environmentService.azureAiSearchApiKey,
    });
    const body = {
      search: searchText,
      count: true,
      vectorQueries: [
        {
          kind: 'text',
          text: searchText,
          fields: 'text_vector',
        },
      ],
      queryType: 'semantic',
      semanticConfiguration:
        this.environmentService.azureOpenAiSemanticConfiguration,
      queryLanguage: 'en-us',
      top: 2,
    };

    return this.http.post<any>(url, body, { headers }).pipe(
      map((data) => {
        if (data && Array.isArray(data.value)) {
          return data.value
            .map((doc: { chunk: string }) => doc.chunk)
            .join('\n');
        }
        return ''; // Return an empty string if the data structure is unexpected
      }),
      // tap((finalResult: string) => {
      //   // This will now log the combined string of the 'chunk' properties
      //   console.log('Final string to be returned:', finalResult);
      // }),
      catchError((error) => {
        console.error('An error occurred in the pipeline:', error);
        return of('');
      })
    );
  }

  private prepareConversationHistory(
    messages: PostRequestBody['messages']
  ): ChatCompletionMessageParam[] {
    return (
      messages?.map((msg) => {
        if (msg.role === 'tool') {
          return {
            role: 'tool',
            content: msg.parts[0].text.value,
            tool_call_id: msg.id,
          };
        }
        const allowedRoles = ['user', 'assistant', 'system'] as const;
        if (allowedRoles.includes(msg.role)) {
          return {
            role: msg.role,
            content: msg.parts[0].text.value,
          };
        }
        throw new Error(`Unsupported message role: ${msg.role}`);
      }) ?? []
    );
  }

  private getSystemPrompt(siteContext: string): string {
    const systemPrompt = `
You are **Edison**, a friendly and knowledgeable AI assistant for a Tesla vehicle listing website called "OnlyUsedTesla.com".

---

## Your Core Purpose
Your main job is to help users with two things:
1.  Answering questions about Tesla vehicles, including their models, trims, specs, features, and the underlying EV technology.
2.  Providing information from our website's content to help with user questions related to site policies, processes, and available listings.

---

## How to Interact with Users
-   **On-Topic Questions:** If a user asks about Tesla, EVs, or topics related to buying and selling cars on our site (like shipping, payment, or listings), use the information you have to provide a helpful and complete answer.
    -   First, check if the provided **site context [site context: (${siteContext})]** has the answer. If it does, use that information.
    -   If the site context is not helpful or is empty, use your general knowledge to answer the question.
    -   If the user asks for available vehicles or to search listings, use the **get_listings** tool.
    -   If the user wants to add a new listing, use the **create_listing** tool.
-   **Greetings & Closings:** Respond to greetings and thank-yous in a warm but brief way.

---

## Tool Usage
-   **get_listings:** Use this tool when a user asks for available vehicles or wants to search or filter listings.
-   **create_listing:** Use this tool when a user provides details to add a new Tesla listing.
    -   **Field Rules for create_listing:**
        -   Only use values the user explicitly provides.
        -   If a required field is missing, set its value to 'null'.
        -   Never make up or infer values (e.g., don't guess a city or reuse values from other fields).
        -   Leave all unspecified fields, like VIN, color, or trim, as 'null'.

---

## Formatting Your Responses
-   Format all your answers using **GitHub-Flavored Markdown (GFM)**.
-   Use headings (##), bold text, italics, and lists to make your responses easy to read.
-   Organize information into short paragraphs.
-   Use tables to compare specs or list attributes.
-   Use fenced code blocks for commands or other code.
-   Feel free to include links when they are helpful.
`;

    return systemPrompt;
  }

  private async getListingsFunction(args: {
    [key: string]: string | number;
  }): Promise<{
    url: string;
    totalCount: number;
    args: { [key: string]: string | number };
  } | null> {
    console.log('Calling getListingFunction with args:', args);
    const baseParams = {
      OrderBy: 'created DESC',
      Skip: 0,
      Top: 9,
      ...args,
    };

    const queryString = stringify(baseParams);
    const query = decorateFilterObj({ ...args, ...baseParams });

    let seperator = '';
    if (query.params.length > 0) {
      seperator = ',';
    }

    const fullUrl = `https://onlyusedtesla.com/used-tesla-for-sale?${queryString}`;
    const apiUrl = `${this.environmentService.url}/Listings/Filter?Filter=${query.params}`;

    try {
      const filters = await lastValueFrom(
        this.http.get<{
          filters: Record<string, Array<{ name: string; count: number }>>;
        }>(apiUrl, {
          headers: new HttpHeaders({
            Accept: 'application/json;ver=1.1',
            Referer: 'https://onlyusedtesla.com/',
          }),
        })
      );
      const totalCount = filters.filters['make']?.[0]?.count ?? 0;
      return { url: fullUrl, totalCount, args };
    } catch (error) {
      console.error('API call error:', error);
      return null;
    }
  }

  fetchListings(
    current: { [key: string]: string | number },
    Skip = 0
  ): Observable<IListingsResponse> {
    const query = decorateFilterObj({ ...current });
    let seperator = '';
    if (query.params.length > 0) {
      seperator = ',';
    }

    const url = `${this.environmentService.url}/Listings?Filter=location.country eq '${this.environmentService.country}'${seperator}${query.params}&Skip=${Skip}&Top=9`;

    return this.http
      .get<IListingsResponse>(url, {
        headers: new HttpHeaders({
          Accept: 'application/json;ver=1.1',
          Referer: 'https://onlyusedtesla.com/',
        }),
      })
      .pipe(
        map((listings) => {
          return listings;
        }),
        catchError((error) => {
          throw error;
        })
      );
  }
}

function decorateFilterObj(current: FilterObject) {
  const filterObj = { ...current };
  // biome-ignore lint/performance/noDelete: <explanation>
  delete filterObj.OrderBy;
  // biome-ignore lint/performance/noDelete: <explanation>
  delete filterObj.Top;

  let skip = 0;

  if (filterObj.Skip) {
    skip = filterObj.Skip;
    // biome-ignore lint/performance/noDelete: <explanation>
    delete filterObj.Skip;
  }
  if (filterObj.model) {
    filterObj['vehicle.model'] = filterObj.model;
    // biome-ignore lint/performance/noDelete: <explanation>
    delete filterObj.model;
  }
  if (filterObj.year) {
    filterObj['vehicle.year'] = Number(filterObj.year);
    // biome-ignore lint/performance/noDelete: <explanation>
    delete filterObj.year;
  }
  if (filterObj.trim) {
    filterObj['vehicle.trim'] = filterObj.trim;
    // biome-ignore lint/performance/noDelete: <explanation>
    delete filterObj.trim;
  }
  if (filterObj.condition) {
    filterObj['vehicle.condition'] = filterObj.condition;
    // biome-ignore lint/performance/noDelete: <explanation>
    delete filterObj.condition;
  }
  if (filterObj.autopilot) {
    filterObj['vehicle.features.autopilot'] = filterObj.autopilot;
    // biome-ignore lint/performance/noDelete: <explanation>
    delete filterObj.autopilot;
  }
  if (filterObj.location) {
    filterObj['location.state'] = filterObj.location;
    // biome-ignore lint/performance/noDelete: <explanation>
    delete filterObj.location;
  }

  const cleanedFilter = cleanObject(filterObj, [null, 0]);

  const params = Object.keys(cleanedFilter)
    .map((prop) => {
      const value = cleanedFilter[prop];

      // Ensure that `value` is a string before using `.includes()`
      if (typeof value === 'string') {
        const operator = value.includes('!') ? 'ne' : 'eq';
        return `${prop} ${operator} '${
          operator === 'ne' ? value.substring(1) : value
        }'`;
      }

      // Handle non-string values appropriately (for numbers, arrays, etc.)
      if (prop === 'vehicle.year') {
        return `${prop} eq ${value}`;
      }
      // If the value is not a string or an array, return the prop as is
      return `${prop} eq '${value}'`;
    })
    .filter(Boolean)
    .join();
  return { cleanedFilter, params, skip };
}

const createListingFieldDescriptions = {
  type: "Type of listing. Usually 'ForSale'.",
  price: 'Price of the vehicle in USD.',
  city: 'City where the vehicle is located.',
  state: 'State where the provided city is in.',
  country: 'Country where the vehicle is located.',
  vin: 'Vehicle Identification Number (VIN) of the Tesla.',
  condition: 'Condition of the vehicle.',
  mileage: 'Mileage of the vehicle in miles.',
  make: 'Make of the vehicle.',
  model: 'Model of the Tesla vehicle.',
  trim: 'Trim level of the Tesla vehicle.',
  year: 'Year of manufacture of the vehicle.',
  leaseMonthsRemaining:
    'Number of months remaining on the lease, if applicable.',
  colour: 'Colour of the vehicle.',
  autopilot: 'Does the vehicle have Autopilot?',
  cleanHistory: 'Does the vehicle have a clean history report?',
  warranty: 'Is the vehicle still under warranty?',
  modifications: 'Has the vehicle been modified?',
  title: 'Does the vehicle have a clear title?',
  fsd: 'Does the vehicle have Full Self-Driving?',
  autopilotType: 'The autopilot type of the Tesla vehicle.',
};
